import isEmpty from 'lodash/isEmpty'
import type { PivotCellTextRenderer } from '../constants/types'
import type { PivotExpandState, PivotNode, PivotResult, PivotRowItem } from '../pivot/types'
import { PivotChartTypeEnum, PivotGrandTotalsEnum } from '@ztechapps/common.contract'
import { PIVOT_GRAND_TOTAL_LABEL } from '../pivot/constants'
import { listPivotColumns } from '../pivot/listPivotColumns'
import { listPivotRows } from '../pivot/listPivotRows'
import { toPivotMemberText, toPivotRowKey } from '../pivot/pivotRow.utils'
import { toPivotMeasureLabel } from '../pivot/toPivotMeasureLabel'
import { PIVOT_ROOT_KEY } from '../pivot/types'

/** One plotted series: its key in every chart row, its legend label and the pivot cell column it reads. */
type PivotChartSeries = { key: string; label: string; measureIndex: number; columnKey: string }
/** One chart row: the `category` label plus one value per series key — `null` where the pivot holds no value. */
type PivotChartRow = Record<string, string | number | null>
/** One pie slice — a category with a positive value. */
type PivotChartSlice = { name: string; value: number }
/** Everything a chart type plots, built from one pivot result. */
type PivotChartData = {
  series: PivotChartSeries[]
  rows: PivotChartRow[]
  pieSlices: PivotChartSlice[]
  droppedCount: number
}

// series keys the Column line chart binds its bar and its line to
const COLUMN_LINE_KEYS = ['m0', 'm1']

const toCategory = (result: PivotResult, item: PivotRowItem, renderCellText?: PivotCellTextRenderer): string =>
  isEmpty(item.path)
    ? PIVOT_GRAND_TOTAL_LABEL
    : item.path
        .map((node, level) => toPivotMemberText(result.rowFields[level].field, node.member, renderCellText))
        .join(' / ')

// Column line reads the first two values off the grand-total column; every other type reads one value per leaf column
const listChartSeries = (
  result: PivotResult,
  chartType: PivotChartTypeEnum,
  measureIndex: number,
  renderCellText?: PivotCellTextRenderer
): PivotChartSeries[] => {
  if (chartType === PivotChartTypeEnum.ColumnLine)
    return COLUMN_LINE_KEYS.map((key, index) => ({
      key,
      label: toPivotMeasureLabel(result.measures[index]),
      measureIndex: index,
      columnKey: PIVOT_ROOT_KEY,
    }))
  // one series per Columns member of the picked value; with no Columns field, the value itself
  const columnLabels = new Map<string, string>()
  const walk = (node: PivotNode, labels: string[]): void => {
    columnLabels.set(node.key, labels.join(' / '))
    node.children.forEach((child) =>
      walk(child, [
        ...labels,
        toPivotMemberText(result.columnFields[labels.length].field, child.member, renderCellText),
      ])
    )
  }
  walk(result.columnRoot, [])
  const { leafColumns } = listPivotColumns({ result, showSubtotals: false, showGrandTotals: PivotGrandTotalsEnum.Off })
  return leafColumns
    .filter((leaf) => leaf.measureIndex === measureIndex)
    .map((leaf, index) => ({
      key: `s${index}`,
      label: columnLabels.get(leaf.columnKey) || toPivotMeasureLabel(result.measures[measureIndex]),
      measureIndex,
      columnKey: leaf.columnKey,
    }))
}

/**
 * Builds the series, rows and pie slices a chart plots from a pivot result. Categories are the rows the grid
 * shows at the current expand depth with subtotals off, or the one grand-total row when no Rows field is set.
 *
 * - `series` / `rows` — Column line plots `m0` and `m1`, the first two values (the result must hold both); every
 *   other type plots one `s<n>` series per leaf column of the `measureIndex` value. A cell with no value is `null`.
 * - `renderCellText` — the viewer's plain-text twin of its cell renderer, so a category or series label reads as the
 *   grid shows it (an order code, not its raw `<code>|<_id>`); a member it declines keeps its label.
 * - `pieSlices` / `droppedCount` — the `measureIndex` value per category: positive values become slices, defined
 *   values at or below zero are only counted, and a category with no value is neither.
 */
export const buildPivotChartData = ({
  result,
  expandState,
  chartType,
  measureIndex,
  renderCellText,
}: {
  result: PivotResult
  expandState: PivotExpandState
  chartType: PivotChartTypeEnum
  measureIndex: number
  renderCellText?: PivotCellTextRenderer
}): PivotChartData => {
  const entries = listPivotRows({ result, expandState, showSubtotals: false }).map((item) => ({
    rowKey: toPivotRowKey(result, item),
    category: toCategory(result, item, renderCellText),
  }))
  const series = listChartSeries(result, chartType, measureIndex, renderCellText)
  const rows = entries.map(({ rowKey, category }) => {
    const row: PivotChartRow = { category }
    series.forEach((entry) => {
      row[entry.key] = result.getValue(rowKey, entry.columnKey, entry.measureIndex) ?? null
    })
    return row
  })
  const totals = entries.map(({ rowKey, category }) => ({
    name: category,
    value: result.getValue(rowKey, PIVOT_ROOT_KEY, measureIndex),
  }))
  const pieSlices = totals.flatMap(({ name, value }): PivotChartSlice[] =>
    value !== undefined && value > 0 ? [{ name, value }] : []
  )
  const droppedCount = totals.filter(({ value }) => value !== undefined && value <= 0).length
  return { series, rows, pieSlices, droppedCount }
}
